import React, { useEffect, useRef, useState } from "react";
import { Layers, Plus } from "lucide-react";
import { AppButton, AppModal, Callout } from "../ui";
import { addTopicResource, deleteTopicResource, updateTopicResource } from "../../utils/smartLearning";
import ScormPlayer from "../SelfPaced/ScormPlayer";
import ResourcePicker from "../LearningResources/ResourcePicker";
import ResourceEditor from "../LearningResources/ResourceEditor";
import ResourceList from "../LearningResources/ResourceList";
import ResourceWorkspace from "../LearningResources/ResourceWorkspace";
import { ADDABLE_KINDS, FULL_EDITOR_HREF, KIND_META } from "../LearningResources/resourceKinds";

/**
 * TopicResourcesDialog — what students get with one topic, when a course's
 * Smart Learning sessions are formed from its syllabus.
 *
 * A topic's resources ride with it into whichever session teaches it (the one
 * that finishes it, if it is split) in every batch — so they are attached here
 * once, not session by session. Each change saves straight away and reaches
 * the sessions running batches have not taught yet.
 *
 * Props:
 *   show, onHide
 *   courseId, topic ({ key, title }), moduleTitle
 *   resources — this topic's CourseTopicResource rows
 *   codingEnabled — offer coding challenges
 *   onChanged() — reload the course's topic resources
 */
const TopicResourcesDialog = ({ show, onHide, courseId, topic, moduleTitle, resources = [], codingEnabled, onChanged }) => {
  const [panel, setPanel] = useState(null); // { mode: 'pick', kind, replace? } | { mode: 'edit', resource }
  const [scorm, setScorm] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [minutes, setMinutes] = useState({});
  const timers = useRef({});

  useEffect(() => {
    if (!show) return;
    setPanel(null);
    setError("");
    setMinutes({});
  }, [show, topic?.key]);

  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

  if (!topic) return null;

  const kinds = ADDABLE_KINDS.filter((k) => codingEnabled || k !== "codingChallenge");
  const rows = resources.map((r) => ({ ...r, durationMinutes: minutes[r._id] ?? r.durationMinutes }));

  const run = async (fn, failure) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      await onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.message || failure);
    } finally {
      setBusy(false);
    }
  };

  const attach = (picked, replacing) => run(async () => {
    await addTopicResource(courseId, {
      topicKey: topic.key,
      kind: picked.kind,
      refId: picked._id,
      title: picked.title,
      contentType: picked.contentType || "",
      durationMinutes: replacing?.durationMinutes || 0,
    });
    if (replacing) await deleteTopicResource(replacing._id);
  }, "The resource was not attached. Try again.");

  const remove = (r) => run(() => deleteTopicResource(r._id), "The resource was not removed. Try again.");

  // A time estimate saves a moment after the last keystroke.
  const setMinutesFor = (r, value) => {
    setMinutes((m) => ({ ...m, [r._id]: value }));
    clearTimeout(timers.current[r._id]);
    timers.current[r._id] = setTimeout(() => {
      updateTopicResource(r._id, { durationMinutes: value }).catch(() => setError("The time estimate was not saved."));
    }, 600);
  };

  const main = (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-muted">
        Whichever session teaches <strong className="tw-text-content">{topic.title}</strong> gets these, in every batch.
        They open by the course’s rules for sessions.
      </p>
      {error && <Callout tone="danger" role="alert">{error}</Callout>}
      <ResourceList
        resources={rows}
        disabled={busy}
        empty="Nothing attached to this topic yet."
        onEdit={(r) => (r.kind === "scorm" ? setScorm(r.refId) : setPanel({ mode: "edit", resource: r }))}
        onReplace={(r) => setPanel({ mode: "pick", kind: r.kind === "scorm" ? "document" : r.kind, replace: r })}
        onRemove={remove}
        onMinutes={(r, _i, v) => setMinutesFor(r, v)}
      />
      <div className="tw-flex tw-flex-wrap tw-gap-2">
        {kinds.map((k) => {
          const { Icon, label } = KIND_META[k];
          return (
            <AppButton key={k} size="sm" variant="outline" onClick={() => setPanel({ mode: "pick", kind: k })} disabled={busy}>
              <Plus size={13} aria-hidden="true" /><Icon size={13} aria-hidden="true" /> {label}
            </AppButton>
          );
        })}
      </div>
    </div>
  );

  let aside = null;
  if (panel?.mode === "pick") {
    aside = (
      <ResourcePicker
        key={`${panel.kind}-${panel.replace?._id || "new"}`}
        kind={panel.kind}
        courseId={courseId}
        initialTab={panel.replace ? "pick" : "create"}
        moduleTitle={moduleTitle}
        sessionTitle={topic.title}
        onPick={(picked) => attach(picked, panel.replace)}
        onClose={() => setPanel(null)}
      />
    );
  } else if (panel?.mode === "edit") {
    aside = (
      <ResourceEditor
        key={panel.resource._id}
        resource={panel.resource}
        courseId={courseId}
        context={{ moduleTitle, sessionTitle: topic.title }}
        onSaved={(saved) => {
          if (saved?.title && saved.title !== panel.resource.title) {
            updateTopicResource(panel.resource._id, { title: saved.title }).then(() => onChanged?.()).catch(() => {});
          }
        }}
        onClose={() => setPanel(null)}
        onOpenFull={(kind, refId) => window.open(FULL_EDITOR_HREF[kind]?.(refId), "_blank", "noopener")}
      />
    );
  }

  return (
    <>
      <AppModal
        show={show}
        onHide={busy ? undefined : onHide}
        size={aside ? "xl" : "lg"}
        title={`Resources for “${topic.title}”`}
        icon={<Layers size={18} aria-hidden="true" />}
        footer={(
          <div className="tw-flex tw-w-full tw-justify-end">
            <AppButton variant="outline" onClick={onHide} disabled={busy}>Done</AppButton>
          </div>
        )}
      >
        <ResourceWorkspace main={main} aside={aside} />
      </AppModal>
      <AppModal show={!!scorm} onHide={() => setScorm(null)} size="xl" title="SCORM preview">
        <div className="tw-h-[70vh]">{scorm && <ScormPlayer lessonId={scorm} />}</div>
      </AppModal>
    </>
  );
};

export default TopicResourcesDialog;
